import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AdminAddons from "@/components/AdminAddons";
import { CircleNotch, ShieldStar, Percent, Users, Package, Lightning, Coins, ArrowsClockwise, Check, Key, Trash, Eye, Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Admin() {
  const { t } = useTranslation();
  const SPOT_LABELS = { gold: t("admin.spotGold"), silver: t("admin.spotSilver"), platinum: t("admin.spotPlatinum") };
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState({});
  const [features, setFeatures] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [creds, setCreds] = useState(null);
  const [credInputs, setCredInputs] = useState({});
  const [customKey, setCustomKey] = useState("");
  const [customVal, setCustomVal] = useState("");
  const [savingCred, setSavingCred] = useState(false);
  const [verifyingStripe, setVerifyingStripe] = useState(false);
  const [stripeResult, setStripeResult] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userForm, setUserForm] = useState(null); // {mode:"create"|"edit", ...fields}
  const [savingUser, setSavingUser] = useState(false);

  const emptyForm = { mode: "create", id: null, name: "", business_name: "", email: "", password: "", plan: "", sub_status: "inactive", credit_balance: 0, role: "owner", send_welcome: true };
  const openCreateUser = () => setUserForm({ ...emptyForm });
  const openEditUser = (u) => setUserForm({ mode: "edit", id: u.id, name: u.name || "", business_name: u.business_name || "",
    email: u.email || "", password: "", plan: u.plan || "", sub_status: u.sub_status || "inactive",
    credit_balance: u.credit_balance ?? 0, role: u.role || "owner", send_welcome: false });
  const setUF = (patch) => setUserForm((f) => ({ ...f, ...patch }));

  const saveUser = async () => {
    const f = userForm;
    if (!f.name.trim()) { toast.error(t("admin.t_nameRequired")); return; }
    if (!f.email.trim()) { toast.error(t("admin.t_emailRequired")); return; }
    setSavingUser(true);
    try {
      if (f.mode === "create") {
        if (!f.password || f.password.length < 6) { toast.error(t("admin.t_passwordMin")); setSavingUser(false); return; }
        const { data } = await api.post("/admin/users", { email: f.email.trim(), password: f.password, name: f.name.trim(),
          business_name: f.business_name.trim(), plan: f.plan || null, send_welcome: !!f.send_welcome });
        toast.success(f.send_welcome ? (data.welcome_email_sent ? t("admin.t_clientCreatedEmail") : t("admin.t_clientCreatedNoEmail")) : t("admin.t_clientCreated"));
      } else {
        const payload = { name: f.name.trim(), business_name: f.business_name.trim(), email: f.email.trim(),
          plan: f.plan || null, sub_status: f.sub_status, credit_balance: Number(f.credit_balance) || 0, role: f.role };
        if (f.password) payload.password = f.password;
        await api.patch(`/admin/users/${f.id}`, payload);
        toast.success(t("admin.t_clientUpdated"));
      }
      setUserForm(null);
      await loadUsers();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingUser(false); }
  };


  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  const openUserDetail = async (id) => {
    try { const { data } = await api.get(`/admin/users/${id}`); setUserDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(t("admin.t_confirmDelete", { name: u.name || u.email }))) return;
    setDeletingUser(u.id);
    try { await api.delete(`/admin/users/${u.id}`); toast.success(t("admin.t_userDeleted")); await loadUsers(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setDeletingUser(null); }
  };

  useEffect(() => {
    api.get("/admin/config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/feature-options").then((r) => setFeatures(r.data.feature_options)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/credentials").then((r) => setCreds(r.data)).catch(() => {});
  }, []);

  const saveCommission = async (ownerId, payload) => {
    try { await api.put(`/admin/owners/${ownerId}/commission`, payload); toast.success(t("admin.t_commissionUpdated")); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveCreds = async () => {
    setSavingCred(true);
    try {
      const payload = { ...credInputs };
      if (customKey.trim() && customVal) payload[customKey.trim()] = customVal;
      const { data } = await api.put("/admin/credentials", { credentials: payload });
      setCreds(data); setCredInputs({}); setCustomKey(""); setCustomVal("");
      toast.success(t("admin.t_credsSaved"));
    } catch (e) { toast.error(apiError(e)); } finally { setSavingCred(false); }
  };

  const verifyStripe = async () => {
    setVerifyingStripe(true); setStripeResult(null);
    try {
      const { data } = await api.get("/admin/stripe/verify");
      setStripeResult(data);
      if (data.ok) toast.success(t("admin.t_stripeConnected", { mode: data.mode }));
      else toast.error(t("admin.t_stripeNotVerified"));
    } catch (e) { toast.error(apiError(e)); } finally { setVerifyingStripe(false); }
  };

  if (!cfg) return <div className="p-8"><CircleNotch className="animate-spin text-gold" size={28} /></div>;

  const setPlan = (id, key, val) => setCfg({ ...cfg, plans: { ...cfg.plans, [id]: { ...cfg.plans[id], [key]: val } } });
  const toggleFeature = (id, fkey) => {
    const cur = cfg.plans[id].feature_keys || [];
    const next = cur.includes(fkey) ? cur.filter((k) => k !== fkey) : [...cur, fkey];
    setPlan(id, "feature_keys", next);
  };
  const setSpot = (metal, val) => setCfg({ ...cfg, metal_spot_prices: { ...cfg.metal_spot_prices, [metal]: parseFloat(val) || 0 } });

  const addPlan = () => {
    const key = `plan_${Date.now().toString(36)}`;
    setCfg({ ...cfg, plans: { ...cfg.plans, [key]: { name: t("admin.newPlanName"), price: 0, renders: 100, products: 10, feature_keys: [], features: [] } } });
    toast.success(t("admin.t_planAdded"));
  };
  const deletePlan = (id) => {
    if (!window.confirm(t("admin.t_confirmDeletePlan", { name: cfg.plans[id]?.name || id }))) return;
    const { [id]: _, ...rest } = cfg.plans;
    setCfg({ ...cfg, plans: rest });
  };
  const setPlanFeaturesText = (id, text) => setPlan(id, "features", text.split("\n").map((s) => s.trim()).filter(Boolean));

  const setCost = (kind, v) => setCfg({ ...cfg, credit_costs: { ...(cfg.credit_costs || {}), [kind]: parseInt(v) || 0 } });
  const addPack = () => setCfg({ ...cfg, credit_packs: [...(cfg.credit_packs || []), { id: `pack_${Date.now().toString(36)}`, name: t("admin.packName2"), credits: 100, price: 10 }] });
  const setPack = (idx, k, v) => setCfg({ ...cfg, credit_packs: (cfg.credit_packs || []).map((p, i) => i === idx ? { ...p, [k]: (k === "name" ? v : parseFloat(v) || 0) } : p) });
  const delPack = (idx) => setCfg({ ...cfg, credit_packs: (cfg.credit_packs || []).filter((_, i) => i !== idx) });

  const fetchPrices = async () => {
    setFetchingPrices(true);
    try {
      const { data } = await api.post("/admin/metal-prices/fetch");
      setCfg((c) => ({ ...c, metal_spot_prices: data.metal_spot_prices }));
      toast.success(t("admin.t_pricesUpdated"));
    } catch (e) { toast.error(apiError(e)); } finally { setFetchingPrices(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", {
        commission_percent: parseFloat(cfg.commission_percent),
        require_byo_ai_key: cfg.require_byo_ai_key,
        plans: cfg.plans,
        metal_spot_prices: cfg.metal_spot_prices,
        credit_costs: cfg.credit_costs,
        credit_packs: cfg.credit_packs,
      });
      setCfg(data); toast.success(t("admin.t_configSaved"));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const statCards = [
    { icon: Users, l: t("admin.owners"), v: stats.owners },
    { icon: Lightning, l: t("admin.activeSubs"), v: stats.active_subscriptions },
    { icon: Package, l: t("admin.products"), v: stats.products },
  ];

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center gap-2 mb-1"><ShieldStar size={22} className="text-gold" weight="fill" /><span className="text-xs uppercase tracking-[0.2em] text-gold">{t("admin.onlyAdmin")}</span></div>
      <h1 className="text-3xl font-bold tracking-tight">{t("admin.title")}</h1>
      <p className="text-neutral-400 mt-1 mb-8">{t("admin.subtitle")}</p>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {statCards.map((c, i) => (
          <div key={i} className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <c.icon size={20} className="text-gold mb-3" weight="duotone" />
            <div className="text-3xl font-bold">{c.v ?? 0}</div><div className="text-sm text-neutral-400">{c.l}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="config">
        <TabsList className="bg-white/5 mb-6">
          <TabsTrigger value="config" data-testid="tab-config">{t("admin.tabConfig")}</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">{t("admin.tabCredentials")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("admin.tabUsers")} ({users.length})</TabsTrigger>
          <TabsTrigger value="addons" data-testid="tab-addons">{t("admin.tabServices")}</TabsTrigger>
        </TabsList>

        <TabsContent value="config">
      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mb-6 space-y-5">
        <h3 className="font-semibold flex items-center gap-2"><Percent size={18} className="text-gold" /> {t("admin.commissionTitle")}</h3>
        <div>
          <Label>{t("admin.commissionPct")}</Label>
          <Input type="number" step="0.1" value={cfg.commission_percent} onChange={(e) => setCfg({ ...cfg, commission_percent: e.target.value })}
            className="mt-2 bg-white/5 border-white/15 w-40" data-testid="commission-input" />
          <p className="text-xs text-neutral-500 mt-2">{t("admin.commissionNote")}</p>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 pt-4">
          <div>
            <div className="font-medium">{t("admin.byoTitle")}</div>
            <div className="text-xs text-neutral-500 max-w-md">{t("admin.byoNote")}</div>
          </div>
          <Switch checked={!!cfg.require_byo_ai_key} onCheckedChange={(v) => setCfg({ ...cfg, require_byo_ai_key: v })} data-testid="require-byo-switch" />
        </div>
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><Coins size={18} className="text-gold" /> {t("admin.metalTitle")}</h3>
          <Button onClick={fetchPrices} disabled={fetchingPrices} size="sm" variant="outline" className="rounded-full border-gold/40 text-gold" data-testid="fetch-prices-btn">
            {fetchingPrices ? <CircleNotch className="animate-spin" /> : <><ArrowsClockwise className="mr-1" /> {t("admin.fetchMarket")}</>}
          </Button>
        </div>
        <p className="text-xs text-neutral-500 mb-3">{t("admin.metalNote")}</p>
        <div className="grid grid-cols-3 gap-3">
          {["gold", "silver", "platinum"].map((m) => (
            <div key={m}>
              <Label className="text-xs">{SPOT_LABELS[m]}</Label>
              <Input type="number" step="0.01" value={cfg.metal_spot_prices?.[m] ?? 0} onChange={(e) => setSpot(m, e.target.value)} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`spot-${m}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{t("admin.plansTitle")}</h3>
          <Button onClick={addPlan} size="sm" variant="outline" className="rounded-full border-gold/30 text-gold hover:bg-gold/10" data-testid="add-plan-btn">{t("admin.addPlan")}</Button>
        </div>
        <div className="space-y-5">
          {Object.entries(cfg.plans).map(([id, p]) => (
            <div key={id} className="bg-white/5 rounded-lg p-4" data-testid={`admin-plan-${id}`}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end mb-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs">{t("admin.planName")}</Label>
                  <Input value={p.name || ""} onChange={(e) => setPlan(id, "name", e.target.value)} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`plan-name-${id}`} />
                </div>
                <div><Label className="text-xs">{t("admin.planPrice")}</Label><Input type="number" value={p.price} onChange={(e) => setPlan(id, "price", parseFloat(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`plan-price-${id}`} /></div>
                <div><Label className="text-xs">{t("admin.maxProducts")}</Label><Input type="number" value={p.products} onChange={(e) => setPlan(id, "products", parseInt(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/15 h-9" /></div>
                <div><Label className="text-xs">Almacenamiento (GB)</Label><Input type="number" value={p.storage_gb ?? 2} onChange={(e) => setPlan(id, "storage_gb", parseFloat(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`plan-storage-${id}`} /></div>
              </div>
              <Label className="text-xs text-neutral-400">{t("admin.includedFeatures")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {features.map((f) => {
                  const on = (p.feature_keys || []).includes(f.key);
                  return (
                    <button key={f.key} onClick={() => toggleFeature(id, f.key)} data-testid={`plan-${id}-feat-${f.key}`}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs ${on ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-neutral-400 hover:border-white/25"}`}>
                      {on && <Check size={11} weight="bold" />} {f.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <Label className="text-xs text-neutral-400">{t("admin.planDesc")}</Label>
                <textarea value={(p.features || []).join("\n")} onChange={(e) => setPlanFeaturesText(id, e.target.value)} rows={4}
                  className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg p-2 text-sm text-neutral-200" data-testid={`plan-desc-${id}`} />
              </div>
              <div className="flex justify-end mt-3">
                <button onClick={() => deletePlan(id)} data-testid={`delete-plan-${id}`}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"><Trash size={13} /> {t("admin.deletePlan")}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-admin-config">
        {saving ? <CircleNotch className="animate-spin" /> : t("admin.saveChanges")}
      </Button>
        </TabsContent>

        <TabsContent value="credentials">
          <div className="border border-gold/30 rounded-xl p-6 bg-gradient-to-b from-gold/[0.06] to-transparent mb-5 space-y-4" data-testid="stripe-guide">
            <div className="flex items-center gap-2"><ShieldStar size={20} className="text-gold" weight="fill" /><h3 className="font-semibold text-gold">{t("admin.stripeGuideTitle")}</h3></div>
            <p className="text-xs text-neutral-400 leading-relaxed">{t("admin.stripeGuideIntro")}</p>
            <ol className="text-xs text-neutral-300 space-y-2 list-decimal pl-5">
              <li>{t("admin.stripeStep1")}</li>
              <li>{t("admin.stripeStep2")}</li>
              <li>{t("admin.stripeStep3")}</li>
              <li>{t("admin.stripeStep4")}</li>
              <li>{t("admin.stripeStep5")} <code className="text-gold break-all">{`${window.location.origin.replace('3000','8001')}`}/api/billing/stripe-webhook</code></li>
              <li>{t("admin.stripeStep6")}</li>
            </ol>
            <div className="rounded-lg bg-red-500/10 border border-red-500/25 p-3 text-xs text-red-200">
              {t("admin.stripeErrorNote")}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={verifyStripe} disabled={verifyingStripe} variant="outline" className="rounded-full border-gold/40 text-gold hover:bg-gold/10" data-testid="verify-stripe-btn">
                {verifyingStripe ? <CircleNotch className="animate-spin mr-1" /> : <Check size={16} className="mr-1" />} {t("admin.testStripe")}
              </Button>
              {stripeResult && (
                stripeResult.ok ? (
                  <span className="text-xs text-emerald-400" data-testid="stripe-verify-ok">{t("admin.stripeOk", { mode: stripeResult.mode, business: stripeResult.business, charges: stripeResult.charges_enabled ? t("admin.chargesOn") : t("admin.chargesOff") })}</span>
                ) : (
                  <span className="text-xs text-red-300" data-testid="stripe-verify-err">✕ {stripeResult.error}</span>
                )
              )}
            </div>
          </div>
          <div className="border border-white/10 rounded-xl p-6 bg-[#121212] space-y-5">
            <div className="flex items-center gap-2"><Key size={20} className="text-gold" /><h3 className="font-semibold">{t("admin.credCenterTitle")}</h3></div>
            <p className="text-xs text-neutral-400">{t("admin.credCenterNote")}</p>
            {!creds ? <CircleNotch className="animate-spin text-gold" /> : (
              <div className="space-y-4">
                {creds.slots.map((s) => (
                  <div key={s.key} data-testid={`cred-slot-${s.key}`}>
                    <Label className="flex items-center gap-2">{s.label}
                      {s.is_set && <span className="text-[10px] text-emerald-400">{t("admin.credSet")} ({s.value_masked})</span>}
                    </Label>
                    <Input type="password" value={credInputs[s.key] ?? ""} onChange={(e) => setCredInputs({ ...credInputs, [s.key]: e.target.value })}
                      placeholder={s.hint} className="mt-1 bg-white/5 border-white/15 font-mono" data-testid={`cred-input-${s.key}`} />
                  </div>
                ))}
                {creds.custom.length > 0 && (
                  <div className="border-t border-white/10 pt-4">
                    <Label className="text-xs text-neutral-400">{t("admin.otherCreds")}</Label>
                    <div className="space-y-3 mt-2">
                      {creds.custom.map((c) => (
                        <div key={c.key} data-testid={`cred-custom-${c.key}`}>
                          <Label className="flex items-center gap-2 font-mono text-xs">{c.key}
                            <span className="text-[10px] text-emerald-400">{c.value_masked}</span>
                          </Label>
                          <Input type="password" value={credInputs[c.key] ?? ""} onChange={(e) => setCredInputs({ ...credInputs, [c.key]: e.target.value })}
                            placeholder={t("admin.newValueEmpty")} className="mt-1 bg-white/5 border-white/15 font-mono" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border-t border-white/10 pt-4">
                  <Label className="text-xs text-neutral-400">{t("admin.addCred")}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input value={customKey} onChange={(e) => setCustomKey(e.target.value)} placeholder={t("admin.credKeyPlaceholder")} className="bg-white/5 border-white/15 font-mono" data-testid="cred-custom-key" />
                    <Input type="password" value={customVal} onChange={(e) => setCustomVal(e.target.value)} placeholder={t("admin.credValPlaceholder")} className="bg-white/5 border-white/15 font-mono" data-testid="cred-custom-val" />
                  </div>
                </div>
                <Button onClick={saveCreds} disabled={savingCred} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-credentials">
                  {savingCred ? <CircleNotch className="animate-spin" /> : t("admin.saveCredentials")}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="flex justify-end mb-3">
            <Button onClick={openCreateUser} size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="add-user-btn">
              <Plus size={15} className="mr-1" /> {t("admin.addClient")}
            </Button>
          </div>
          <div className="border border-white/10 rounded-xl p-3 bg-[#121212] overflow-x-auto max-w-full">
            <table className="w-full min-w-[780px] text-xs" data-testid="users-table">
              <thead><tr className="text-neutral-400 text-left border-b border-white/10">
                <th className="py-2 pr-3">{t("admin.colUser")}</th><th className="pr-3">{t("admin.colRole")}</th><th className="pr-3">{t("admin.colPlan")}</th>
                <th className="pr-3">{t("admin.colSub")}</th><th className="pr-3">{t("admin.colCommission")}</th><th className="pr-3">{t("admin.colCredits")}</th><th className="pr-3">{t("admin.colProd")}</th><th className="sticky right-0 bg-[#121212] pl-3 text-right">{t("admin.colActions")}</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`user-row-${u.id}`}>
                    <td className="py-2 pr-4">
                      <div className="font-medium">{u.name || "—"}</div>
                      <div className="text-xs text-neutral-500">{u.email}{u.business_name ? ` · ${u.business_name}` : ""}</div>
                    </td>
                    <td className="pr-4"><span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-gold/20 text-gold" : "bg-white/10 text-neutral-300"}`}>{u.role}</span></td>
                    <td className="pr-4 capitalize">{u.plan || "—"}</td>
                    <td className="pr-4"><span className={`text-xs px-2 py-0.5 rounded-full ${u.sub_status === "active" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-neutral-400"}`}>{u.sub_status}</span></td>
                    <td className="pr-4">
                      {u.role === "admin" ? <span className="text-neutral-600">—</span> : (
                        <div className="flex items-center gap-1">
                          <input type="number" step="0.1" defaultValue={u.commission_rate_percent} onBlur={(e) => saveCommission(u.id, { commission_rate_percent: parseFloat(e.target.value) })}
                            className="w-14 bg-white/5 border border-white/15 rounded px-1 py-0.5 text-xs" data-testid={`commission-rate-${u.id}`} />
                          <span className="text-xs text-neutral-500">%</span>
                          <select defaultValue={u.billing_cadence} onChange={(e) => saveCommission(u.id, { cadence: e.target.value })}
                            className="bg-white/5 border border-white/15 rounded px-1 py-0.5 text-xs" data-testid={`commission-cadence-${u.id}`}>
                            <option value="WEEKLY">{t("admin.weekly")}</option>
                            <option value="MONTHLY">{t("admin.monthly")}</option>
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="pr-3">{u.credit_balance ?? 0}</td>
                    <td className="pr-3">{u.products}</td>
                    <td className="sticky right-0 bg-[#121212] pl-3 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.6)]">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openUserDetail(u.id)} title={t("admin.viewDetails")} data-testid={`view-user-${u.id}`}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-gold hover:bg-white/10"><Eye size={15} /></button>
                        {u.role !== "admin" && (
                          <button onClick={() => openEditUser(u)} title={t("admin.editClient")} data-testid={`edit-user-${u.id}`}
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-gold hover:bg-white/10"><PencilSimple size={15} /></button>
                        )}
                        {u.role !== "admin" && (
                          <button onClick={() => deleteUser(u)} disabled={deletingUser === u.id} title={t("admin.deleteUser")} data-testid={`delete-user-${u.id}`}
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                            {deletingUser === u.id ? <CircleNotch size={15} className="animate-spin" /> : <Trash size={15} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="addons">
          <AdminAddons />
        </TabsContent>
      </Tabs>

      <Dialog open={!!userDetail} onOpenChange={(o) => !o && setUserDetail(null)}>
        <DialogContent className="bg-[#151515] border-white/10 text-neutral-200 max-w-lg" data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle>{userDetail?.name || userDetail?.email}</DialogTitle></DialogHeader>
          {userDetail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {[
                  [t("admin.detailEmail"), userDetail.email],
                  [t("admin.detailBusiness"), userDetail.business_name || "—"],
                  [t("admin.detailRole"), userDetail.role],
                  [t("admin.detailPlan"), userDetail.plan || "—"],
                  [t("admin.detailSub"), userDetail.sub_status],
                  [t("admin.detailCommission"), `${userDetail.commission_rate_percent}% · ${userDetail.billing_cadence === "WEEKLY" ? t("admin.weekly") : t("admin.monthly")}`],
                  [t("admin.detailBilling"), userDetail.billing_status],
                  [t("admin.detailCard"), userDetail.has_card ? t("admin.yes") : t("admin.no")],
                  [t("admin.detailProducts"), userDetail.products],
                  [t("admin.detailLogos"), userDetail.logos],
                  [t("admin.detailDesigns"), userDetail.designs],
                  [t("admin.detailRenders"), userDetail.renders_used],
                  [t("admin.detailAiKey"), userDetail.ai_key_set ? t("admin.configured") : t("admin.notConfigured")],
                  [t("admin.detailMeshyKey"), userDetail.meshy_key_set ? t("admin.configured") : t("admin.notConfigured")],
                  [t("admin.detailEmail"), `${userDetail.gmail_connected ? t("admin.connected") : t("admin.notConnected")} (${userDetail.email_method})`],
                  [t("admin.detailSignup"), userDetail.created_at ? new Date(userDetail.created_at).toLocaleDateString() : "—"],
                ].map(([k, v], i) => (
                  <div key={i}><div className="text-[11px] text-neutral-500">{k}</div><div className="font-medium">{String(v)}</div></div>
                ))}
              </div>
              {userDetail.integrations?.length > 0 && (
                <div><div className="text-[11px] text-neutral-500 mb-1">{t("admin.integrations")}</div>
                  <div className="flex flex-wrap gap-1.5">{userDetail.integrations.map((i, idx) => (
                    <span key={idx} className={`text-xs px-2 py-0.5 rounded-full ${i.connected ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-neutral-400"}`}>{i.platform}</span>
                  ))}</div>
                </div>
              )}
              {userDetail.role !== "admin" && (
                <Button variant="outline" onClick={() => { const u = users.find((x) => x.id === userDetail.id); setUserDetail(null); if (u) deleteUser(u); }}
                  className="w-full mt-2 border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="detail-delete-user">
                  <Trash size={14} className="mr-1" /> {t("admin.deleteUserData")}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!userForm} onOpenChange={(o) => !o && setUserForm(null)}>
        <DialogContent className="bg-[#151515] border-white/10 text-neutral-200 max-w-md" data-testid="user-form-dialog">
          <DialogHeader><DialogTitle>{userForm?.mode === "create" ? t("admin.createClientTitle") : t("admin.editClientTitle")}</DialogTitle>
            <DialogDescription className="text-xs text-neutral-400">{userForm?.mode === "create" ? t("admin.createClientDesc") : t("admin.editClientDesc")}</DialogDescription>
          </DialogHeader>
          {userForm && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">{t("admin.name")}</Label>
                <Input value={userForm.name} onChange={(e) => setUF({ name: e.target.value })} className="mt-1 bg-white/5 border-white/15 h-9" data-testid="user-form-name" />
              </div>
              <div>
                <Label className="text-xs">{t("admin.businessName")}</Label>
                <Input value={userForm.business_name} onChange={(e) => setUF({ business_name: e.target.value })} className="mt-1 bg-white/5 border-white/15 h-9" data-testid="user-form-business" />
              </div>
              <div>
                <Label className="text-xs">{t("admin.email")}</Label>
                <Input type="email" value={userForm.email} onChange={(e) => setUF({ email: e.target.value })} className="mt-1 bg-white/5 border-white/15 h-9" data-testid="user-form-email" />
              </div>
              <div>
                <Label className="text-xs">{userForm.mode === "create" ? t("admin.passwordCreate") : t("admin.passwordEdit")}</Label>
                <Input type="text" autoComplete="new-password" value={userForm.password} onChange={(e) => setUF({ password: e.target.value })}
                  placeholder={userForm.mode === "edit" ? "••••••" : ""} className="mt-1 bg-white/5 border-white/15 h-9" data-testid="user-form-password" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("admin.detailPlan")}</Label>
                  <select value={userForm.plan || ""} onChange={(e) => setUF({ plan: e.target.value })}
                    className="mt-1 w-full bg-white/5 border border-white/15 rounded px-2 h-9 text-sm" data-testid="user-form-plan">
                    <option value="">{t("admin.noPlan")}</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                {userForm.mode === "edit" && (
                  <div>
                    <Label className="text-xs">{t("admin.detailSub")}</Label>
                    <select value={userForm.sub_status} onChange={(e) => setUF({ sub_status: e.target.value })}
                      className="mt-1 w-full bg-white/5 border border-white/15 rounded px-2 h-9 text-sm" data-testid="user-form-substatus">
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="trialing">trialing</option>
                      <option value="past_due">past_due</option>
                      <option value="canceled">canceled</option>
                    </select>
                  </div>
                )}
              </div>
              {userForm.mode === "edit" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t("admin.creditBalance")}</Label>
                    <Input type="number" min="0" value={userForm.credit_balance} onChange={(e) => setUF({ credit_balance: e.target.value })}
                      className="mt-1 bg-white/5 border-white/15 h-9" data-testid="user-form-credits" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("admin.role")}</Label>
                    <select value={userForm.role} onChange={(e) => setUF({ role: e.target.value })}
                      className="mt-1 w-full bg-white/5 border border-white/15 rounded px-2 h-9 text-sm" data-testid="user-form-role">
                      <option value="owner">{t("admin.roleOwner")}</option>
                      <option value="admin">{t("admin.roleAdmin")}</option>
                    </select>
                  </div>
                </div>
              )}
              {userForm.mode === "create" && (
                <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-pointer" data-testid="user-form-welcome-label">
                  <input type="checkbox" checked={userForm.send_welcome} onChange={(e) => setUF({ send_welcome: e.target.checked })} data-testid="user-form-welcome" />
                  {t("admin.sendWelcome")}
                </label>
              )}
              <Button onClick={saveUser} disabled={savingUser} className="w-full bg-gold text-obsidian hover:bg-gold/90 font-semibold mt-1" data-testid="user-form-save">
                {savingUser ? <CircleNotch size={15} className="animate-spin mr-1" /> : null}
                {userForm.mode === "create" ? t("admin.createClient") : t("admin.saveChanges")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
